import { DataGridPro, GridRowId, GridRowParams } from '@mui/x-data-grid-pro';
import React, { useEffect, useMemo, useState } from 'react';
import { getGroupColumns, getGroupRows } from './helpers';
import { TableGroupDoc, TableGroupMapping, TablesGroup } from '../landing-page-types';
import { JobSearchBar } from './JobSearchBar';
import { matchesSearchQuery, SearchableValue } from './search-helpers';

// Params for GroupsDataGrid component
interface JobGroupsDataGridParams<T> {
  detailContent: (params: GridRowParams<TablesGroup<T>>) => JSX.Element,
  jobs: T[],
  getJobId: (job: T) => string,
  groupMappings: TableGroupMapping[],
  updateGroupToDelete: (group: TablesGroup<unknown>) => void,
  groups: TableGroupDoc[]
  refreshTable: () => void,
  loading: boolean
  groupSync: (groupId: string) => Promise<void>,
  // all values of a job which the search bar can match against
  getJobSearchValues: (job: T) => SearchableValue[],
  // placeholder text for the search bar, describing what can be searched on this page
  searchPlaceholder: string,
}

export const JobGroupsDataGrid = function<T> ({
  detailContent, groupMappings, groups, jobs, getJobId, updateGroupToDelete, refreshTable, loading, groupSync,
  getJobSearchValues, searchPlaceholder,
}: JobGroupsDataGridParams<T>) {
  // path determines which group sync is rendered
  const [searchQuery, setSearchQuery] = useState<string>('');
  // groups which are currently expanded to show their jobs
  const [expandedGroupIds, setExpandedGroupIds] = useState<GridRowId[]>([]);

  const query = searchQuery.trim();

  // generate rows for groups datagrid - in this case, each row corresponds to a group of jobs
  const allGroups: TablesGroup<T>[] = useMemo(() => {
    const groupRows = getGroupRows<T>(
      groupMappings,
      groups,
      jobs,
      getJobId,
    );

    // Filter out groups that have mappings but no tables in current view
    // Keep groups with zero mappings (truly empty groups)
    return groupRows.filter((group) => {
      const hasAnyMappings = groupMappings.some((mapping) => mapping.groupId === group.groupId);
      return !hasAnyMappings || group.tables.length > 0;
    });
  }, [groups, groupMappings, jobs]);

  // groups shown in the datagrid, narrowed down to the jobs matching the current search query
  const rows: TablesGroup<T>[] = useMemo(() => {
    if (!query) return allGroups;

    return allGroups
      // a group whose name matches the query keeps all of its jobs, otherwise only matching jobs are kept
      .map((group) => (matchesSearchQuery([group.groupName], query)
        ? group
        : { ...group, tables: group.tables.filter((job) => matchesSearchQuery(getJobSearchValues(job), query)) }))
      // while searching, groups without any matching jobs are hidden
      .filter((group) => group.tables.length > 0);
  }, [allGroups, query, getJobSearchValues]);

  // expand every matching group while searching so that matching jobs are visible without any extra clicks
  useEffect(() => {
    // rows is intentionally not a dependency - groups are only auto expanded/collapsed when the query itself changes
    setExpandedGroupIds(query ? rows.map(({ groupName }) => groupName) : []);
  }, [query]);

  // Generate columns for datagrid
  const columns = useMemo(
    () => getGroupColumns<T>(updateGroupToDelete, groups, refreshTable, groupSync),
    [updateGroupToDelete, groups, refreshTable, groupSync],
  );

  // number of jobs which are currently visible across all groups
  const matchCount = useMemo(() => rows.reduce((count, { tables }) => count + tables.length, 0), [rows]);

  return (
    <div className="flex flex-col h-full gap-3">
      <JobSearchBar
        query={searchQuery}
        updateQuery={setSearchQuery}
        placeholder={searchPlaceholder}
        matchCount={matchCount}
        totalCount={jobs.length}
      />
      <div className="flex-1 min-h-0">
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={({ groupName }) => groupName}
          getDetailPanelContent={detailContent}
          getDetailPanelHeight={() => 'auto'}
          detailPanelExpandedRowIds={expandedGroupIds}
          onDetailPanelExpandedRowIdsChange={setExpandedGroupIds}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: query ? 'No jobs match your search' : 'No rows' }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'group', sort: 'asc' }],
            },
          }}
        />
      </div>
    </div>
  );
};
